import { useState, useEffect } from 'react';
import { validateMatchData, ValidationError } from '../utils';
import * as matchStorage from '../storage/matchStorage';

export interface SetupData {
  matchId: string;
  context: string;
  centerId: string;
  centerName: string;
  courtId: string;
  courtName: string;
  liveScoreUrl: string;
  scoringType: string;
  resultCallbackUrl: string;
}

const COMPACT_KEY_MAP: Record<string, keyof SetupData> = {
  id: 'matchId',
  ctx: 'context',
  ci: 'centerId',
  cn: 'centerName',
  ti: 'courtId',
  tn: 'courtName',
  lsu: 'liveScoreUrl',
  st: 'scoringType',
  rcu: 'resultCallbackUrl',
};

function decodeBase64url(str: string): string {
  let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
  while (base64.length % 4) {
    base64 += '=';
  }
  return atob(base64);
}

function expandCompactKeys(compact: Record<string, any>): SetupData {
  const expanded: Record<string, any> = {};
  for (const [key, value] of Object.entries(compact)) {
    const fullKey = COMPACT_KEY_MAP[key];
    if (fullKey) {
      expanded[fullKey] = value;
    }
  }
  return expanded as SetupData;
}

interface UseMatchDataReturn {
  matchData: any;
  setupData: SetupData | null;
  validationErrors: ValidationError[];
  isValidating: boolean;
  hasErrors: boolean;
  isInitialized: boolean;
  dataSource: string;
  retry: () => void;
  clearData: () => void;
}

/**
 * Hook pour la gestion des données de match.
 *
 * Ordre de priorité des sources, du plus fort au plus faible :
 *   1. `?data=`  - payload complet passé par MySquash (lien de lancement)
 *   2. `?d=`     - configuration compacte issue d'un QR code de court
 *   3. `pendingSetupData`  (sessionStorage) - QR scanné plus tôt dans la session
 *   4. `pendingMatchData`  (sessionStorage) - passe-plat MatchSetupScreen / refresh
 *   5. `matchData`         (localStorage)   - filet de sécurité
 *
 * L'URL prime volontairement sur le storage : un résidu de session ne doit
 * jamais masquer les paramètres d'un nouveau lancement.
 */
export function useMatchData(): UseMatchDataReturn {
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [isValidating, setIsValidating] = useState(true);
  const [matchData, setMatchData] = useState<any>(null);
  const [setupData, setSetupData] = useState<SetupData | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [dataSource, setDataSource] = useState<string>('');

  // Masquer les paramètres de l'URL après lecture (délai : évite un conflit
  // avec le hot-reload du dev server react-scripts)
  const scheduleUrlCleanup = () => {
    setTimeout(() => {
      window.history.replaceState(null, '', window.location.pathname);
    }, 100);
  };

  const validateAndSetData = () => {
    setIsValidating(true);

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const dataParam = urlParams.get('data');
      const dParam = urlParams.get('d');

      let rawData = null;
      let source = '';

      // === Source 1 (priorité absolue) : payload complet dans l'URL (?data=) ===
      // Un lien de lancement doit TOUJOURS l'emporter sur ce qu'un match
      // précédent a laissé dans le storage. Sans cette priorité, un
      // `pendingMatchData` ou un `pendingSetupData` résiduel masquait
      // silencieusement les paramètres du nouveau match.
      if (dataParam) {
        try {
          // URLSearchParams.get() has already decoded the value: a second
          // decodeURIComponent() throws on a literal '%' ("100%") and silently
          // rewrites the escapes of URLs in the payload (an avatar's %20).
          rawData = JSON.parse(dataParam);
          source = 'URL parameters';
          // Un payload complet remplace une configuration QR en attente
          matchStorage.clearSetupInput();
          // Écrire AVANT de nettoyer l'URL : garantit que les données
          // survivent à un re-render de React et à un refresh.
          matchStorage.saveMatchInput(rawData);
          scheduleUrlCleanup();
        } catch (e) {
          console.warn('Données URL invalides:', e);
        }
      }

      // === Source 2 : configuration QR dans l'URL (?d=, base64url compact) ===
      if (!rawData && dParam) {
        try {
          const decoded = decodeBase64url(dParam);
          const compact = JSON.parse(decoded);
          const expanded = expandCompactKeys(compact);
          // Un scan frais remplace le payload d'un lancement précédent, mais ne
          // touche PAS au match en cours : c'est à la modale de récupération de
          // demander à l'arbitre ce qu'il veut en faire.
          matchStorage.clearMatchInput();
          matchStorage.saveSetupInput(expanded);
          setSetupData(expanded);
          setDataSource('qr-scan');
          // Errors of an earlier pass (no data yet) must not outlive the court configuration
          setValidationErrors([]);
          scheduleUrlCleanup();
          setIsValidating(false);
          return;
        } catch (e) {
          console.warn('Invalid QR data:', e);
        }
      }

      // === Source 3 : configuration QR scannée plus tôt dans la session ===
      if (!rawData) {
        const pendingSetup = matchStorage.readSetupInput();
        if (pendingSetup) {
          setSetupData(pendingSetup);
          setDataSource('qr-scan');
          setValidationErrors([]);
          setIsValidating(false);
          return;
        }
      }

      // Source historique : window.matchData
      // Cas d'usage théorique : embedding ou webview injectant les données avant le mount.
      // Pas de cas d'usage concret identifié dans ce projet - commenté pour simplifier.
      //
      // if (!rawData && (window as any).matchData) {
      //   rawData = (window as any).matchData;
      //   source = 'window.matchData';
      // }

      // === Source 4 : storage (passe-plat MatchSetupScreen, survie au refresh) ===
      if (!rawData) {
        const stored = matchStorage.readMatchInput();
        if (stored) {
          rawData = stored.data;
          source = stored.source;
        }
      }

      setDataSource(source);

      // Valider les données
      const errors = validateMatchData(rawData);
      
      if (errors.length === 0 && rawData) {
        // Données valides
        setMatchData(rawData);
        setValidationErrors([]);
        setIsInitialized(true);
      } else {
        // Erreurs de validation ou pas de données
        if (!rawData) {
          setValidationErrors([{
            field: 'Données',
            message: 'Aucune donnée de match trouvée. Vérifiez que l\'application a été lancée correctement.'
          }]);
        } else {
          setValidationErrors(errors);
        }
        setMatchData(null);
        setIsInitialized(false);
      }
    } catch (error) {
      // Erreur de parsing ou autre
      setValidationErrors([{
        field: 'Application',
        message: 'Erreur lors de la lecture des données: ' + (error as Error).message
      }]);
      setMatchData(null);
      setIsInitialized(false);
    }
    
    setIsValidating(false);
  };

  // Validation initiale
  useEffect(() => {
    validateAndSetData();
  }, []);

  // TODO: Envisager la suppression de ce listener postMessage
  // Ce listener n'est utilisé que par les pages de test d'intégration (test-integration/).
  // En production, les données arrivent via URL parameters, sessionStorage ou localStorage.
  // Risque de sécurité : aucune vérification d'origine (event.origin) n'est effectuée,
  // ce qui permet à n'importe quelle iframe/popup d'injecter des données de match.
  //
  // useEffect(() => {
  //   const handleMessage = (event: MessageEvent) => {
  //     if (event.data.type === 'MATCH_DATA') {
  //       sessionStorage.setItem('pendingMatchData', JSON.stringify(event.data.data));
  //       validateAndSetData();
  //     } else if (event.data.type === 'LAUNCH_MATCH') {
  //       sessionStorage.setItem('pendingMatchData', JSON.stringify(event.data.matchData));
  //       validateAndSetData();
  //     }
  //   };
  //
  //   window.addEventListener('message', handleMessage);
  //   return () => window.removeEventListener('message', handleMessage);
  // }, []);

  const retry = () => {
    validateAndSetData();
  };

  const clearData = () => {
    // Nettoyer toutes les sources de données
    delete (window as any).matchData;
    matchStorage.clearAll();
    setMatchData(null);
    setSetupData(null);
    setIsInitialized(false);
    setValidationErrors([]);
    setIsValidating(false);
  };

  return {
    matchData,
    setupData,
    validationErrors,
    isValidating,
    hasErrors: validationErrors.length > 0,
    isInitialized,
    dataSource,
    retry,
    clearData
  };
}